import React, { useState, useEffect, useCallback } from 'react';
import {
  HeartPulse, CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw,
  Search, ChevronDown, ChevronUp, Phone, Megaphone, MessageSquare,
  Building2, Mail, Users, Map, Activity, BookOpen, Sparkles, KeyRound, Boxes,
  CreditCard, Gauge, Zap, Link2, Power, ShieldAlert, SlidersHorizontal, Lock, Brain, Handshake
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { fetchWithCsrf } from '../lib/fetchWithCsrf';

// ─── Step config ─────────────────────────────────────────────────────────────

const STEP_CONFIG = {
  account_created:    { label: 'Account Created',     icon: Users,        color: 'indigo'  },
  welcome_email:      { label: 'Welcome Email Sent',  icon: Mail,         color: 'blue'    },
  company_profile:    { label: 'Company Profile',     icon: Building2,    color: 'purple'  },
  phone_purchased:    { label: 'Phone Purchased',     icon: Phone,        color: 'cyan'    },
  campaign_created:   { label: 'Campaign Created',    icon: Megaphone,    color: 'amber'   },
  campaign_activated: { label: 'Campaign Activated',  icon: Activity,     color: 'orange'  },
  knowledge_base:     { label: 'Knowledge Base',      icon: BookOpen,     color: 'violet'  },
  tour_completed:     { label: 'Tour Completed',      icon: Map,          color: 'teal'    },
  first_lead:         { label: 'First Lead Imported', icon: Users,        color: 'emerald' },
  first_message:      { label: 'First Message Sent',  icon: MessageSquare,color: 'green'   },
};

const STEP_ORDER = [
  'account_created', 'welcome_email', 'company_profile', 'phone_purchased',
  'campaign_created', 'campaign_activated', 'knowledge_base', 'tour_completed', 'first_lead', 'first_message',
];

// ─── Progress bar ────────────────────────────────────────────────────────────

function ProgressDots({ steps }) {
  return (
    <div className="flex items-center gap-0.5">
      {STEP_ORDER.map((key, i) => {
        const done = steps[key];
        return (
          <React.Fragment key={key}>
            <div
              title={`${STEP_CONFIG[key].label}: ${done ? 'Done' : 'Pending'}`}
              className={`w-2.5 h-2.5 rounded-full ${done ? 'bg-green-500' : 'bg-gray-300'}`}
            />
            {i < STEP_ORDER.length - 1 && (
              <div className={`w-3 h-px ${done ? 'bg-green-300' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Health badge ────────────────────────────────────────────────────────────

function HealthBadge({ health }) {
  const cfg = {
    healthy:     { label: 'Healthy',     classes: 'bg-green-100 text-green-800',  Icon: CheckCircle },
    in_progress: { label: 'In Progress', classes: 'bg-yellow-100 text-yellow-800', Icon: Clock },
    at_risk:     { label: 'At Risk',     classes: 'bg-red-100 text-red-800',      Icon: AlertTriangle },
  };
  const { label, classes, Icon } = cfg[health] || cfg.in_progress;

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${classes}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// ─── Onboarding status badge ──────────────────────────────────────────────────
// Distinct from Health (which scores activation breadth). This is the literal
// "did they finish the onboarding wizard" answer: complete = onboarding_complete
// flag true; in_progress = signed in but not finished; not_started = never signed in.

const ONBOARDING_CFG = {
  complete:    { label: 'Complete',    classes: 'bg-green-100 text-green-800',  Icon: CheckCircle },
  in_progress: { label: 'In Progress', classes: 'bg-blue-100 text-blue-800',    Icon: Clock },
  not_started: { label: 'Not Started', classes: 'bg-gray-100 text-gray-500',    Icon: XCircle },
};

function OnboardingBadge({ status }) {
  const { label, classes, Icon } = ONBOARDING_CFG[status] || ONBOARDING_CFG.not_started;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${classes}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// ─── Anthropic workspace badge + management card ────────────────────────────

const ANTHROPIC_STATUS_CFG = {
  not_provisioned: { label: 'Not set up',  classes: 'bg-gray-100 text-gray-600',     Icon: AlertTriangle },
  workspace_only:  { label: 'Key pending', classes: 'bg-yellow-100 text-yellow-800', Icon: KeyRound },
  key_set:         { label: 'Active',      classes: 'bg-green-100 text-green-800',   Icon: CheckCircle },
  key_invalid:     { label: 'Key invalid', classes: 'bg-red-100 text-red-800',       Icon: XCircle },
};

function AnthropicBadge({ anthropic }) {
  const cfg = ANTHROPIC_STATUS_CFG[anthropic?.status] || ANTHROPIC_STATUS_CFG.not_provisioned;
  const { label, classes, Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${classes}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function AnthropicCard({ tenant, onChange }) {
  const a = tenant.anthropic || { status: 'not_provisioned' };
  const [keyInput, setKeyInput] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const run = async (action, label) => {
    if (busy) return;
    setBusy(label);
    setError(null);
    try {
      await action();
      onChange?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const apiCall = async (path, method, body) => {
    const res = await fetchWithCsrf(path, {
      method,
      headers: { 'content-type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  };

  const handleProvision = () =>
    run(() => apiCall('/api/admin/anthropic/workspace', 'POST', { tenant_id: tenant.tenant_id }), 'provision');

  const handleSaveKey = (replace = false) => {
    if (!keyInput.trim()) { setError('Paste the API key first'); return; }
    return run(async () => {
      await apiCall('/api/admin/anthropic/key', 'PUT', {
        tenant_id: tenant.tenant_id, api_key: keyInput.trim(), replace,
      });
      setKeyInput('');
    }, 'save');
  };

  const handleValidate = () =>
    run(() => apiCall('/api/admin/anthropic/key/validate', 'POST', { tenant_id: tenant.tenant_id }), 'validate');

  const handleClear = () => {
    if (!window.confirm('Clear the stored Anthropic key for this tenant? They will fall back to the global key until a new one is pasted.')) return;
    return run(() => apiCall('/api/admin/anthropic/key', 'DELETE', { tenant_id: tenant.tenant_id }), 'clear');
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-gray-400" />
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Anthropic</span>
        </div>
        <AnthropicBadge anthropic={a} />
      </div>

      {a.status === 'not_provisioned' && (
        <div>
          <p className="text-xs text-gray-600 mb-3">
            Create a dedicated Anthropic Workspace so this tenant's usage shows separately in the Anthropic Console.
          </p>
          <button
            onClick={handleProvision}
            disabled={busy === 'provision'}
            className="w-full px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {busy === 'provision' ? 'Provisioning…' : 'Provision Workspace'}
          </button>
        </div>
      )}

      {a.status === 'workspace_only' && (
        <div className="space-y-2">
          <div className="text-xs">
            <p className="text-gray-700"><span className="font-medium">Workspace:</span> {a.workspace_name}</p>
            <p className="text-gray-400 font-mono break-all">{a.workspace_id}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800">
            Open Anthropic Console → this workspace → create an API key → paste below.
          </div>
          <input
            type="password"
            value={keyInput}
            onChange={e => setKeyInput(e.target.value)}
            placeholder="sk-ant-…"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            onClick={() => handleSaveKey(false)}
            disabled={busy === 'save' || !keyInput.trim()}
            className="w-full px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {busy === 'save' ? 'Validating…' : 'Save & Validate Key'}
          </button>
        </div>
      )}

      {(a.status === 'key_set' || a.status === 'key_invalid') && (
        <div className="space-y-2">
          <div className="text-xs">
            <p className="text-gray-700"><span className="font-medium">Workspace:</span> {a.workspace_name}</p>
            <p className="text-gray-400 font-mono break-all">{a.workspace_id}</p>
            <p className="text-gray-500 mt-1">
              Key set {a.key_set_at ? formatDate(a.key_set_at) : '—'}
              {a.key_last_validated_at && <> · Validated {timeAgo(a.key_last_validated_at)}</>}
            </p>
            {a.key_last_error && (
              <p className="text-red-600 mt-1">Last error: {a.key_last_error}</p>
            )}
          </div>

          {a.status === 'key_invalid' && (
            <div>
              <input
                type="password"
                value={keyInput}
                onChange={e => setKeyInput(e.target.value)}
                placeholder="Paste replacement sk-ant-… key"
                className="w-full mb-2 px-2 py-1.5 border border-gray-300 rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={() => handleSaveKey(true)}
                disabled={busy === 'save' || !keyInput.trim()}
                className="w-full px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {busy === 'save' ? 'Validating…' : 'Replace Key'}
              </button>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleValidate}
              disabled={!!busy}
              className="flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded hover:bg-gray-50 disabled:opacity-50"
            >
              {busy === 'validate' ? 'Validating…' : 'Re-validate'}
            </button>
            <button
              onClick={handleClear}
              disabled={!!busy}
              className="flex-1 px-3 py-1.5 bg-white border border-red-200 text-red-600 text-xs font-medium rounded hover:bg-red-50 disabled:opacity-50"
            >
              {busy === 'clear' ? 'Clearing…' : 'Clear Key'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800 flex items-start gap-1">
          <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

// ─── Messaging kill switch ───────────────────────────────────────────────────
// GlobalAdmin hard stop for ALL outbound AI messaging on this account. When off,
// every send path (outreach, follow-ups, replies, queued holds) suppresses sends
// and the customer sees an "account paused" banner. The customer cannot re-enable
// themselves — only this toggle can. Every flip is written to the audit trail.

function MessagingCard({ tenant, onChange }) {
  const m = tenant.messaging || { enabled: true };
  const enabled = m.enabled !== false;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');
  const [showReason, setShowReason] = useState(false);

  const toggle = async (nextEnabled) => {
    if (busy) return;
    if (!nextEnabled && !showReason) {
      // First click on Disable reveals the reason field; second click confirms.
      setShowReason(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetchWithCsrf('/api/admin/messaging/toggle', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenant.tenant_id,
          enabled: nextEnabled,
          reason: nextEnabled ? undefined : (reason.trim() || undefined),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setReason('');
      setShowReason(false);
      onChange?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-lg border p-4 ${enabled ? 'bg-white border-gray-200' : 'bg-red-50 border-red-300'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Power className={`w-4 h-4 ${enabled ? 'text-gray-400' : 'text-red-500'}`} />
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Messaging</span>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
          enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {enabled ? <CheckCircle className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
          {enabled ? 'Enabled' : 'Paused'}
        </span>
      </div>

      {enabled ? (
        <div className="space-y-2">
          {!showReason ? (
            <>
              <p className="text-xs text-gray-600">
                All outbound AI messaging is active. Pausing stops every send immediately
                (outreach, follow-ups, replies, and queued messages) and shows the customer a
                paused-account banner.
              </p>
              <button
                onClick={() => toggle(false)}
                disabled={busy}
                className="w-full px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                Pause Messaging
              </button>
            </>
          ) : (
            <>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Reason (optional, saved to the audit trail)"
                rows={2}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => toggle(false)}
                  disabled={busy}
                  className="flex-1 px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {busy ? 'Pausing…' : 'Confirm Pause'}
                </button>
                <button
                  onClick={() => { setShowReason(false); setReason(''); }}
                  disabled={busy}
                  className="px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-red-800">
            <p className="font-medium">Account paused by admin.</p>
            {m.disabled_at && <p className="text-red-600 mt-0.5">Since {formatDateTime(m.disabled_at)}</p>}
            {m.disabled_reason && <p className="text-red-700 mt-1 italic">"{m.disabled_reason}"</p>}
          </div>
          <button
            onClick={() => toggle(true)}
            disabled={busy}
            className="w-full px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {busy ? 'Restoring…' : 'Restore Messaging'}
          </button>
        </div>
      )}

      {error && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800 flex items-start gap-1">
          <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

// ─── Expanded detail ─────────────────────────────────────────────────────────

function TenantDetail({ tenant, onChange }) {
  const { steps, details } = tenant;

  return (
    <tr>
      <td colSpan={10} className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        {/* Step checklist */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {STEP_ORDER.map(key => {
            const cfg = STEP_CONFIG[key];
            const done = steps[key];
            const Icon = cfg.icon;

            return (
              <div
                key={key}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${
                  done
                    ? 'bg-green-50 border-green-200'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                  done ? 'bg-green-500' : 'bg-gray-200'
                }`}>
                  {done
                    ? <CheckCircle className="w-4 h-4 text-white" />
                    : <Icon className="w-3.5 h-3.5 text-gray-400" />
                  }
                </div>
                <span className={`text-sm font-medium ${done ? 'text-green-800' : 'text-gray-500'}`}>
                  {cfg.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Extra details */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Welcome Email */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Welcome Email</span>
            </div>
            {details.welcome_email ? (
              <div className="space-y-1">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  details.welcome_email.status === 'sent'
                    ? 'bg-green-100 text-green-800'
                    : details.welcome_email.status === 'failed'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {details.welcome_email.status === 'sent' && <CheckCircle className="w-3 h-3" />}
                  {details.welcome_email.status === 'failed' && <XCircle className="w-3 h-3" />}
                  {details.welcome_email.status === 'pending' && <Clock className="w-3 h-3" />}
                  {details.welcome_email.status}
                </span>
                {details.welcome_email.sent_at && (
                  <p className="text-xs text-gray-400">Sent {formatDate(details.welcome_email.sent_at)}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No welcome email scheduled</p>
            )}
          </div>

          {/* Campaigns */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Megaphone className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Campaigns ({details.campaigns.length})
              </span>
            </div>
            {details.campaigns.length > 0 ? (
              <div className="space-y-2">
                {details.campaigns.slice(0, 3).map(c => (
                  <div key={c.id} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 truncate max-w-[140px]">{c.name}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                ))}
                {details.campaigns.length > 3 && (
                  <p className="text-xs text-gray-400">+{details.campaigns.length - 3} more</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No campaigns created</p>
            )}
          </div>

          {/* Activity */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Activity</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Leads</span>
                <span className="text-sm font-semibold text-gray-900">{details.lead_count}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Messages</span>
                <span className="text-sm font-semibold text-gray-900">{details.message_count}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Phone Numbers</span>
                <span className="text-sm font-semibold text-gray-900">{details.phone_count}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Knowledge Base</span>
                <span className="text-sm font-semibold text-gray-900">{details.kb_count}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Tour</span>
                <span className={`text-sm font-semibold ${details.tour_completed ? 'text-green-600' : 'text-gray-400'}`}>
                  {details.tour_completed ? 'Completed' : 'Not done'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Last Active</span>
                <span className={`text-sm font-semibold ${
                  !details.last_sign_in ? 'text-red-500'
                    : daysSince(details.last_sign_in) > 3 ? 'text-yellow-600'
                    : 'text-green-600'
                }`}>
                  {details.last_sign_in ? `${timeAgo(details.last_sign_in)} · ${formatDateTime(details.last_sign_in)}` : 'Never'}
                </span>
              </div>
            </div>
          </div>

          {/* Anthropic workspace + key */}
          <AnthropicCard tenant={tenant} onChange={onChange} />

          {/* Messaging kill switch */}
          <MessagingCard tenant={tenant} onChange={onChange} />

          {/* DNC scrub: enable + per-tenant FTC SAN / ORG ID */}
          <DncCard tenant={tenant} onChange={onChange} />
        </div>
      </td>
    </tr>
  );
}

// ─── DNC scrub card ──────────────────────────────────────────────────────────
// GlobalAdmin per-tenant control that sits next to the messaging kill switch.
// Enables DNC scrubbing for the tenant and stores their FTC SAN + ORG ID. The
// actual Blacklist Alliance master API key is platform-wide (Internal Tools tab).
// A tenant can only scrub once: platform key set + include_dnc on + SAN + ORG ID.

function DncCard({ tenant, onChange }) {
  const d = tenant.dnc || { enabled: false, has_san: false, has_org_id: false };
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [san, setSan] = useState('');
  const [orgId, setOrgId] = useState('');

  const call = async (path, body) => {
    const res = await fetchWithCsrf(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  };

  const run = async (fn, label) => {
    if (busy) return;
    setBusy(label);
    setError(null);
    try { await fn(); onChange?.(); }
    catch (e) { setError(e.message); }
    finally { setBusy(null); }
  };

  const toggle = (enabled) => run(
    () => call('/api/dnc/admin/toggle', { tenant_id: tenant.tenant_id, enabled }),
    'toggle',
  );

  const saveCreds = () => {
    if (!san.trim() && !orgId.trim()) { setError('Enter a SAN and/or ORG ID'); return; }
    return run(async () => {
      await call('/api/dnc/admin/creds', {
        tenant_id: tenant.tenant_id,
        ...(san.trim() ? { san: san.trim() } : {}),
        ...(orgId.trim() ? { org_id: orgId.trim() } : {}),
      });
      setSan('');
      setOrgId('');
    }, 'creds');
  };

  const enabled = d.enabled === true;

  return (
    <div className={`rounded-lg border p-4 ${enabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldAlert className={`w-4 h-4 ${enabled ? 'text-cyan-600' : 'text-gray-400'}`} />
          <span className="text-sm font-semibold text-gray-900">DNC Scrub</span>
        </div>
        <button
          onClick={() => toggle(!enabled)}
          disabled={busy === 'toggle'}
          className={`text-xs font-semibold px-3 py-1 rounded-full disabled:opacity-50 ${
            enabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          {busy === 'toggle' ? '…' : enabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>

      <div className="space-y-1 text-xs mb-3">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">SAN on file</span>
          <span className={`font-medium ${d.has_san ? 'text-green-600' : 'text-gray-400'}`}>{d.has_san ? 'Yes' : 'No'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-600">ORG ID on file</span>
          <span className={`font-medium ${d.has_org_id ? 'text-green-600' : 'text-gray-400'}`}>{d.has_org_id ? 'Yes' : 'No'}</span>
        </div>
      </div>

      <p className="text-[11px] text-gray-500 mb-2">
        Enter this tenant's FTC Do Not Call SAN and ORG ID. Leave a field blank to keep its current value.
      </p>
      <div className="space-y-2">
        <input
          type="text"
          value={san}
          onChange={e => setSan(e.target.value)}
          placeholder={d.has_san ? 'Replace SAN' : 'SAN'}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500"
        />
        <input
          type="text"
          value={orgId}
          onChange={e => setOrgId(e.target.value)}
          placeholder={d.has_org_id ? 'Replace ORG ID' : 'ORG ID'}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500"
        />
        <button
          onClick={saveCreds}
          disabled={busy === 'creds' || (!san.trim() && !orgId.trim())}
          className="w-full px-3 py-2 bg-cyan-700 text-white text-sm font-medium rounded-lg hover:bg-cyan-800 disabled:opacity-50 transition-colors"
        >
          {busy === 'creds' ? 'Saving…' : 'Save SAN / ORG ID'}
        </button>
      </div>

      {error && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800 flex items-start gap-1">
          <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
  });
}

function timeAgo(iso) {
  if (!iso) return 'Never';
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

const HEALTH_FILTERS = [
  { value: 'all',         label: 'All Tenants'  },
  { value: 'at_risk',     label: 'At Risk'      },
  { value: 'in_progress', label: 'In Progress'  },
  { value: 'healthy',     label: 'Healthy'      },
];

const ONBOARDING_FILTERS = [
  { value: 'all',         label: 'Onboarding: All'         },
  { value: 'not_started', label: 'Onboarding: Not Started' },
  { value: 'in_progress', label: 'Onboarding: In Progress' },
  { value: 'complete',    label: 'Onboarding: Complete'    },
];

// ─── Internal Tools tab ──────────────────────────────────────────────────────
// SurFox-internal Claude tools (Hunter, onboarding, surf agent). Same Anthropic
// workspace/key management as tenants, keyed by tool slug instead of tenant_id.

function ToolCard({ tool, onChange }) {
  const a = tool.anthropic || { status: 'not_provisioned' };
  const [keyInput, setKeyInput] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const run = async (action, label) => {
    if (busy) return;
    setBusy(label);
    setError(null);
    try {
      await action();
      onChange?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const apiCall = async (path, method, body) => {
    const res = await fetchWithCsrf(path, {
      method,
      headers: { 'content-type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  };

  const base = `/api/admin/internal-tools/${tool.slug}`;
  const handleProvision = () => run(() => apiCall(`${base}/workspace`, 'POST'), 'provision');
  const handleSaveKey = (replace = false) => {
    if (!keyInput.trim()) { setError('Paste the API key first'); return; }
    return run(async () => {
      await apiCall(`${base}/key`, 'PUT', { api_key: keyInput.trim(), replace });
      setKeyInput('');
    }, 'save');
  };
  const handleValidate = () => run(() => apiCall(`${base}/key/validate`, 'POST'), 'validate');
  const handleClear = () => {
    if (!window.confirm(`Clear the stored Anthropic key for ${tool.display_name}? It will fall back to its per-tool env var, then the global key.`)) return;
    return run(() => apiCall(`${base}/key`, 'DELETE'), 'clear');
  };

  // ── Service-key tools (non-Anthropic, e.g. DNC Scrub / Blacklist Alliance) ──
  // One platform-wide third-party key. No workspace, no sk-ant validation.
  if (tool.kind === 'service') {
    const s = tool.service || { key_set: false };
    const saveServiceKey = (replace = false) => {
      if (!keyInput.trim()) { setError('Paste the API key first'); return; }
      return run(async () => {
        await apiCall(`${base}/service-key`, 'PUT', { api_key: keyInput.trim(), replace });
        setKeyInput('');
      }, 'save');
    };
    const clearServiceKey = () => {
      if (!window.confirm(`Clear the stored key for ${tool.display_name}?`)) return;
      return run(() => apiCall(`${base}/service-key`, 'DELETE'), 'clear');
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-900">{tool.display_name}</span>
          </div>
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${s.key_set ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {s.key_set ? 'Key set' : 'Not set'}
          </span>
        </div>
        {tool.description && <p className="text-xs text-gray-500 mb-1">{tool.description}</p>}
        <p className="text-[11px] font-mono text-gray-400 mb-3">{tool.slug}</p>

        {!s.key_set ? (
          <div className="space-y-2">
            <input
              type="password"
              value={keyInput}
              onChange={e => setKeyInput(e.target.value)}
              placeholder="Paste the Blacklist Alliance master API key"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={() => saveServiceKey(false)}
              disabled={busy === 'save' || !keyInput.trim()}
              className="w-full px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {busy === 'save' ? 'Saving…' : 'Save Key'}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">Key set {s.key_set_at ? formatDate(s.key_set_at) : '—'}</p>
            <input
              type="password"
              value={keyInput}
              onChange={e => setKeyInput(e.target.value)}
              placeholder="Paste replacement key"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="flex gap-2">
              <button
                onClick={() => saveServiceKey(true)}
                disabled={busy === 'save' || !keyInput.trim()}
                className="flex-1 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {busy === 'save' ? 'Saving…' : 'Replace Key'}
              </button>
              <button
                onClick={clearServiceKey}
                disabled={!!busy}
                className="flex-1 px-3 py-1.5 bg-white border border-red-200 text-red-600 text-xs font-medium rounded hover:bg-red-50 disabled:opacity-50"
              >
                {busy === 'clear' ? 'Clearing…' : 'Clear Key'}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800 flex items-start gap-1">
            <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-semibold text-gray-900">{tool.display_name}</span>
        </div>
        <AnthropicBadge anthropic={a} />
      </div>
      {tool.description && <p className="text-xs text-gray-500 mb-1">{tool.description}</p>}
      <p className="text-[11px] font-mono text-gray-400 mb-3">{tool.slug}</p>

      {a.status === 'not_provisioned' && (
        <div>
          <p className="text-xs text-gray-600 mb-3">
            Create a dedicated Anthropic Workspace so this tool's usage shows separately in the Console.
          </p>
          <button
            onClick={handleProvision}
            disabled={busy === 'provision'}
            className="w-full px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {busy === 'provision' ? 'Provisioning…' : 'Provision Workspace'}
          </button>
        </div>
      )}

      {a.status === 'workspace_only' && (
        <div className="space-y-2">
          <div className="text-xs">
            <p className="text-gray-700"><span className="font-medium">Workspace:</span> {a.workspace_name}</p>
            <p className="text-gray-400 font-mono break-all">{a.workspace_id}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800">
            Open Anthropic Console → this workspace → create an API key → paste below.
          </div>
          <input
            type="password"
            value={keyInput}
            onChange={e => setKeyInput(e.target.value)}
            placeholder="sk-ant-…"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            onClick={() => handleSaveKey(false)}
            disabled={busy === 'save' || !keyInput.trim()}
            className="w-full px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {busy === 'save' ? 'Validating…' : 'Save & Validate Key'}
          </button>
        </div>
      )}

      {(a.status === 'key_set' || a.status === 'key_invalid') && (
        <div className="space-y-2">
          <div className="text-xs">
            <p className="text-gray-700"><span className="font-medium">Workspace:</span> {a.workspace_name || '—'}</p>
            {a.workspace_id && <p className="text-gray-400 font-mono break-all">{a.workspace_id}</p>}
            <p className="text-gray-500 mt-1">
              Key set {a.key_set_at ? formatDate(a.key_set_at) : '—'}
              {a.key_last_validated_at && <> · Validated {timeAgo(a.key_last_validated_at)}</>}
            </p>
            {a.key_last_error && (
              <p className="text-red-600 mt-1">Last error: {a.key_last_error}</p>
            )}
          </div>

          {a.status === 'key_invalid' && (
            <div>
              <input
                type="password"
                value={keyInput}
                onChange={e => setKeyInput(e.target.value)}
                placeholder="Paste replacement sk-ant-… key"
                className="w-full mb-2 px-2 py-1.5 border border-gray-300 rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={() => handleSaveKey(true)}
                disabled={busy === 'save' || !keyInput.trim()}
                className="w-full px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {busy === 'save' ? 'Validating…' : 'Replace Key'}
              </button>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleValidate}
              disabled={!!busy}
              className="flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded hover:bg-gray-50 disabled:opacity-50"
            >
              {busy === 'validate' ? 'Validating…' : 'Re-validate'}
            </button>
            <button
              onClick={handleClear}
              disabled={!!busy}
              className="flex-1 px-3 py-1.5 bg-white border border-red-200 text-red-600 text-xs font-medium rounded hover:bg-red-50 disabled:opacity-50"
            >
              {busy === 'clear' ? 'Clearing…' : 'Clear Key'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800 flex items-start gap-1">
          <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

function InternalToolsPanel() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await fetchWithCsrf('/api/admin/internal-tools');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setTools(data.tools || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Internal Tools</h2>
        <p className="text-sm text-gray-600 mt-1">
          SurFox-internal Claude tools. Give each its own Anthropic workspace + key so its spend is
          attributable in the Console. A tool with no key here falls back to its per-tool env var,
          then the global key.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center">
          <XCircle className="w-5 h-5 text-red-600 mr-2" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : tools.length === 0 ? (
        <div className="text-center py-12">
          <Boxes className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No internal tools registered</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tools.map(t => <ToolCard key={t.slug} tool={t} onChange={load} />)}
        </div>
      )}
    </div>
  );
}

// ─── Billing & Usage panel ───────────────────────────────────────────────────

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(`${String(d).slice(0, 10)}T00:00:00Z`);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

function UsageBar({ pct }) {
  if (pct == null) return <span className="text-xs text-gray-400">no limit</span>;
  const clamped = Math.min(pct, 100);
  const color = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-green-500';
  const textColor = pct >= 100 ? 'text-red-700' : pct >= 80 ? 'text-amber-700' : 'text-gray-600';
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${clamped}%` }} />
      </div>
      <span className={`text-xs font-medium tabular-nums ${textColor}`}>{pct}%</span>
    </div>
  );
}

// ─── Warm-up ramp cells ──────────────────────────────────────────────────────
// The monthly plan limit is not the only ceiling. For the first 21 days after a
// tenant's first campaign activation, a per-day carrier warm-up cap binds too,
// and it is usually the one that actually stops sending. A tenant at 4% of their
// monthly allotment can be completely stalled on ramp day 3, so these two
// columns sit next to the monthly usage rather than on a separate screen.

function RampDayCell({ row }) {
  if (row.warmup_in_ramp) {
    const pct = Math.min((row.warmup_day / row.warmup_total_days) * 100, 100);
    return (
      <div className="min-w-[92px]">
        <div className="text-sm text-gray-900 tabular-nums">
          Day {row.warmup_day}
          <span className="text-xs text-gray-400"> / {row.warmup_total_days}</span>
        </div>
        <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }
  // Past day 21: warm, and no cap. Say so rather than showing a blank.
  if (row.warmup_day != null) {
    return <span className="text-xs text-gray-400" title={`Day ${row.warmup_day} since first activation`}>graduated</span>;
  }
  // No anchor at all — never activated a campaign, or predates first_activated_at.
  return <span className="text-xs text-gray-300" title="No campaign has ever been activated for this tenant">not started</span>;
}

// ─── Post-ramp pacing cell ───────────────────────────────────────────────────
// The ramp columns beside this one read "graduated" for every tenant we have, so
// this is the ceiling that is actually in force. Shows what the tenant is SET to
// and how much of today's allowance they have spent.
//
// A null cap is not missing data, it is the fail-open state: the recalc has not
// reached them yet, or they have no allowance or billing anchor configured. It is
// labelled "no cap" rather than left blank so it cannot be mistaken for a gap.
const PACE_LABELS = {
  spread_evenly: 'Spread evenly',
  custom_cap: 'Custom cap',
  max_speed: 'Max speed',
};

function PaceCell({ row }) {
  if (!row.pace_mode) {
    return <span className="text-xs text-gray-300" title="No pacing row for this tenant, so no post-ramp ceiling">not set</span>;
  }

  const label = PACE_LABELS[row.pace_mode] || row.pace_mode;
  const cap = row.pace_cap;
  const used = row.pace_used_today;

  // Max speed is the one worth spotting from across the room: it is the only
  // mode with no ceiling, and it needed an explicit risk acknowledgement.
  if (row.pace_mode === 'max_speed') {
    return (
      <div className="min-w-[120px]">
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
          title={row.pace_acknowledged_risk_at
            ? `Risk acknowledged ${new Date(row.pace_acknowledged_risk_at).toLocaleDateString()}`
            : 'No ceiling'}
        >
          {label}
        </span>
        {used != null && (
          <div className="mt-1 text-xs text-gray-500 tabular-nums">{used.toLocaleString()} sent today</div>
        )}
      </div>
    );
  }

  const pct = row.pace_pct ?? 0;
  const clamped = Math.min(pct, 100);
  const color = row.pace_at_cap ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-green-500';

  return (
    <div className="min-w-[130px]">
      <div className="text-xs text-gray-600">{label}</div>
      <div className={`text-sm tabular-nums ${row.pace_at_cap ? 'text-red-700' : 'text-gray-700'}`}>
        {used == null ? '—' : used.toLocaleString()}
        <span className="text-gray-400"> / {cap == null ? 'no cap' : cap.toLocaleString()}</span>
      </div>
      {cap != null && (
        <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full ${color} rounded-full`} style={{ width: `${clamped}%` }} />
        </div>
      )}
    </div>
  );
}

function RampUsageCell({ row }) {
  if (!row.warmup_in_ramp) return <span className="text-xs text-gray-300">—</span>;
  const pct = row.warmup_pct ?? 0;
  const clamped = Math.min(pct, 100);
  const color = row.warmup_at_cap ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-green-500';
  const textColor = row.warmup_at_cap ? 'text-red-700' : pct >= 80 ? 'text-amber-700' : 'text-gray-600';
  return (
    <div className="min-w-[130px]">
      <div className={`text-sm tabular-nums ${textColor}`}>
        {row.warmup_used.toLocaleString()}
        <span className="text-gray-400"> / {row.warmup_daily_cap.toLocaleString()}</span>
        {row.warmup_window_capped && (
          <span
            className="ml-1 text-[10px] uppercase tracking-wide text-amber-600"
            title={`Weekly 15,000 ceiling binds first — daily cap is ${row.warmup_headline_cap.toLocaleString()}`}
          >
            wk
          </span>
        )}
      </div>
      <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${clamped}%` }} />
      </div>
    </div>
  );
}

function SubBadge({ status }) {
  const cfg = {
    active:    'bg-green-100 text-green-800',
    trialing:  'bg-blue-100 text-blue-800',
    past_due:  'bg-amber-100 text-amber-800',
    canceled:  'bg-gray-100 text-gray-600',
    suspended: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg[status] || 'bg-gray-100 text-gray-600'}`}>
      {status || 'unknown'}
    </span>
  );
}

// ─── Talk Track Tuning ───────────────────────────────────────────────────────
// Are customers actually fine-tuning their AI, or launching it untouched?
//
// Two signals, and they are not equal. Test conversations mean they watched their
// AI work. RECALIBRATIONS mean they watched it, disagreed, and rewrote the
// messaging — that costs effort and only happens on conviction, so it is the one
// worth reading. A tenant with tests and no recalibrations may just be clearing
// the gate; a tenant with recalibrations is genuinely tuning.
function TuningPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithCsrf('/api/admin/tuning/overview');
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setData(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-16 text-center text-sm text-gray-400">Loading tuning activity…</div>;
  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
      </div>
    );
  }

  const t = data?.totals || {};
  const tenants = data?.tenants || [];
  const pct = t.tenants ? Math.round((t.tenants_tuning / t.tenants) * 100) : 0;

  const cards = [
    { label: 'Accounts that have tested', value: `${t.tenants_tuning || 0} of ${t.tenants || 0}`, sub: `${pct}% have ever run a test conversation · ${t.people_testing || 0} ${t.people_testing === 1 ? 'person' : 'people'}`, Icon: Users, tone: 'text-indigo-600 bg-indigo-50' },
    { label: 'Accounts recalibrating', value: t.tenants_recalibrating || 0, sub: 'rewrote their messaging after seeing it run', Icon: SlidersHorizontal, tone: 'text-green-600 bg-green-50' },
    { label: 'Test conversations', value: t.test_conversations || 0, sub: 'driven to a natural ending and counted', Icon: MessageSquare, tone: 'text-gray-600 bg-gray-100' },
    { label: 'Campaigns held by the gate', value: t.campaigns_gated || 0, sub: `${t.campaigns_skipped || 0} waived as near-identical`, Icon: Lock, tone: 'text-amber-600 bg-amber-50' },
  ];

  return (
    <div>
      <div className="mb-5 flex items-start justify-between gap-4">
        <p className="max-w-2xl text-sm text-gray-500">
          Who is actually fine-tuning their AI before it goes live. A <span className="font-medium text-gray-700">recalibration</span> is
          the signal that matters: it means they read their AI&rsquo;s replies, disagreed, and rewrote the talk track.
          {data?.gate_live_from && (
            <span className="block mt-1 text-xs text-gray-400">
              Launch gate live since {new Date(data.gate_live_from).toLocaleString()}. Campaigns created before then are exempt.
            </span>
          )}
        </p>
        <button onClick={load} className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {cards.map(({ label, value, sub, Icon, tone }) => (
          <div key={label} className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-2">
              <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}><Icon className="h-4 w-4" /></span>
              <span className="text-xs font-medium text-gray-500">{label}</span>
            </div>
            <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
            <div className="mt-0.5 text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      {/* The finding, stated plainly rather than left for the reader to infer. */}
      {t.recalibrations === 0 && (
        <div className="mb-5 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <span className="font-semibold">No account has ever recalibrated a talk track.</span> Every campaign on the
            platform is running the messaging it was first generated with. Either the copy is landing, or nobody is
            looking closely enough to disagree with it.
          </span>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2.5 font-medium">Account</th>
              <th className="px-4 py-2.5 font-medium">Campaigns</th>
              <th className="px-4 py-2.5 font-medium">Test conversations</th>
              <th className="px-4 py-2.5 font-medium">Who tested</th>
              <th className="px-4 py-2.5 font-medium">Recalibrations</th>
              <th className="px-4 py-2.5 font-medium">Scenarios covered</th>
              <th className="px-4 py-2.5 font-medium">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {tenants.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No accounts.</td></tr>
            )}
            {tenants.map((r) => (
              <tr key={r.tenant_id} className={`border-b border-gray-100 last:border-0 ${r.tuned ? '' : 'bg-gray-50/40'}`}>
                <td className="px-4 py-2.5">
                  <div className="font-medium text-gray-800">{r.tenant_name}</div>
                  <div className="text-xs text-gray-400">{r.subscription_status || 'no subscription'}</div>
                </td>
                <td className="px-4 py-2.5 text-gray-600">
                  <span className="font-mono">{r.campaigns_total}</span>
                  <span className="text-xs text-gray-400">
                    {r.campaigns_live > 0 && <> · {r.campaigns_live} live</>}
                    {r.campaigns_gated > 0 && <> · <span className="text-amber-600">{r.campaigns_gated} held</span></>}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <span className={`font-mono ${r.test_conversations ? 'text-gray-800' : 'text-gray-300'}`}>{r.test_conversations}</span>
                  {r.campaigns_cleared > 0 && <span className="ml-1 text-xs text-green-600">{r.campaigns_cleared} cleared</span>}
                </td>
                <td className="px-4 py-2.5">
                  {r.testers.length ? (
                    <div className="space-y-0.5">
                      {r.testers.slice(0, 2).map((p) => (
                        <div key={p.id} className="text-xs text-gray-700" title={p.email || p.id}>
                          {p.name || <span className="font-mono text-gray-400">{String(p.id).slice(0, 8)}</span>}
                          <span className="ml-1 text-gray-400">×{p.tests}</span>
                        </div>
                      ))}
                      {r.testers.length > 2 && <div className="text-[11px] text-gray-400">+{r.testers.length - 2} more</div>}
                    </div>
                  ) : r.tests_unattributed > 0 ? (
                    // Honest about the gap rather than implying nobody did it.
                    <span className="text-xs text-gray-400" title="Banked before we recorded who ran the test">
                      {r.tests_unattributed} before tracking
                    </span>
                  ) : <span className="text-xs text-gray-300">—</span>}
                </td>
                <td className="px-4 py-2.5">
                  {r.recalibrations > 0 ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">
                      <SlidersHorizontal className="h-3 w-3" /> {r.recalibrations} across {r.tracks_recalibrated} track{r.tracks_recalibrated === 1 ? '' : 's'}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">never</span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  {r.scenarios_tested.length ? (
                    <div className="flex flex-wrap gap-1">
                      {r.scenarios_tested.slice(0, 3).map((s) => (
                        <span key={s} className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">{s}</span>
                      ))}
                      {r.scenarios_tested.length > 3 && <span className="text-[11px] text-gray-400">+{r.scenarios_tested.length - 3}</span>}
                    </div>
                  ) : <span className="text-xs text-gray-300">—</span>}
                </td>
                <td className="px-4 py-2.5 text-xs text-gray-500">
                  {r.last_activity ? new Date(r.last_activity).toLocaleDateString() : <span className="text-gray-300">never</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BillingUsagePanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('enforced_pct');
  const [sortDir, setSortDir] = useState('desc');

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);
      const res = await fetchWithCsrf('/api/billing/tenants-overview');
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setRows(data.tenants || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(p => (p === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'tenant_name' ? 'asc' : 'desc'); }
  };

  const filtered = rows
    .filter(r => !search || (r.tenant_name || '').toLowerCase().includes(search.toLowerCase()))
    .slice()
    .sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'tenant_name') cmp = (a.tenant_name || '').localeCompare(b.tenant_name || '');
      else cmp = ((a[sortKey] ?? -1) - (b[sortKey] ?? -1));
      return sortDir === 'asc' ? cmp : -cmp;
    });

  // Headroom tiles and the Used bar both read enforced_pct, NOT live_pct. The send
  // gate (_shared/smsLimit.ts) compares tenants.current_month_sms_count against
  // sms_limit + topup, so that ratio is the only one that predicts a cutoff. The
  // two diverge because trigger_update_sms_count fires AFTER INSERT on messages
  // with no DELETE decrement: measured 2026-08-22, Da Silva was 47,342 enforced vs
  // 45,983 surviving rows against 1,370 message_deleted audit rows in the cycle.
  // Charting live_pct there showed 91.6% while the customer's own meter
  // (AuthContext.jsx:25, which reads the enforced counter) showed 94.7%.
  const nearLimit = rows.filter(r => r.enforced_pct != null && r.enforced_pct >= 80 && r.enforced_pct < 100).length;
  const overLimit = rows.filter(r => r.enforced_pct != null && r.enforced_pct >= 100).length;
  const mismatches = rows.filter(r => r.counter_mismatch).length;

  const SortHeader = ({ k, children, align = 'left' }) => (
    <th
      onClick={() => handleSort(k)}
      className={`px-4 py-3 text-${align} text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortKey === k && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </span>
    </th>
  );

  return (
    <>
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Tenants</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{rows.length}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Near limit (≥80%)</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{nearLimit}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Over limit (≥100%)</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{overLimit}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500 flex items-center gap-1">
            Counter mismatches
            <span title="Enforced counter diverges from the live in-cycle count: deleted leads if enforced is higher, reset-anchor drift if lower">
              <AlertTriangle className="w-3.5 h-3.5 text-gray-400" />
            </span>
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{mismatches}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="relative w-72 max-w-full">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tenants…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="flex items-center px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error ? (
          <div className="p-6 text-sm text-red-600">{error}</div>
        ) : loading ? (
          <div className="p-6 text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No tenants found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <SortHeader k="tenant_name">Tenant</SortHeader>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Cycle start</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Resets</th>
                  <SortHeader k="total_limit" align="right">Limit</SortHeader>
                  <SortHeader k="enforced_count" align="right">Enforced</SortHeader>
                  <SortHeader k="live_cycle_count" align="right">Live (cycle)</SortHeader>
                  <SortHeader k="hot_leads_cycle_count" align="right">Hot leads (cycle)</SortHeader>
                  <SortHeader k="enforced_pct">Used</SortHeader>
                  <SortHeader k="pace_pct">Today vs pace</SortHeader>
                  <SortHeader k="warmup_day">Ramp day</SortHeader>
                  <SortHeader k="warmup_pct">Today vs ramp cap</SortHeader>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((r) => (
                  <tr key={r.tenant_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{r.tenant_name || '—'}</div>
                      {(r.is_demo || r.is_test) && (
                        <span className="text-[10px] uppercase tracking-wide text-gray-400">
                          {[r.is_demo && 'demo', r.is_test && 'test'].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 capitalize">{r.plan || '—'}</td>
                    <td className="px-4 py-3"><SubBadge status={r.subscription_status} /></td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{fmtDate(r.cycle_start)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{fmtDate(r.next_reset_date)}</td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-700">
                      {r.sms_limit == null ? '—' : r.sms_limit.toLocaleString()}
                      {r.topup_messages_current_cycle > 0 && (
                        <span className="text-xs text-gray-400"> +{r.topup_messages_current_cycle.toLocaleString()}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-700">
                      <span className="inline-flex items-center gap-1 justify-end">
                        {r.counter_mismatch && (
                          <span title="Diverges from the live count: deleted leads if higher, reset-anchor drift if lower">
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                          </span>
                        )}
                        {r.enforced_count.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-900 font-medium">
                      {r.live_cycle_count == null ? '—' : r.live_cycle_count.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-900 font-medium">
                      {r.hot_leads_cycle_count == null ? '—' : r.hot_leads_cycle_count.toLocaleString()}
                    </td>
                    <td className="px-4 py-3"><UsageBar pct={r.enforced_pct} /></td>
                    <td className="px-4 py-3"><PaceCell row={r} /></td>
                    <td className="px-4 py-3"><RampDayCell row={r} /></td>
                    <td className="px-4 py-3"><RampUsageCell row={r} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
          <span className="font-medium text-gray-500">Used</span> is the ENFORCED counter over the limit, because that is the only number that can stop a send.{' '}
          <span className="font-medium text-gray-500">Enforced</span> = tenants.current_month_sms_count, incremented by the trigger_update_sms_count
          trigger on every messages INSERT (in + out, failed included). It has no DELETE decrement, so deleting a lead erases its
          message rows but not the allotment they consumed.{' '}
          <span className="font-medium text-gray-500">Live (cycle)</span> = messages in + out since cycle start, excluding failed. A
          diagnostic, not the billing figure. Enforced sitting ABOVE live is normal and usually means deleted leads; enforced sitting
          BELOW live means the reset anchor drifted from the real billing date. A mismatch (⚠) flags either.
          <div className="mt-1">
            <span className="font-medium text-gray-500">Ramp day</span> = days since the tenant&rsquo;s first campaign activation
            (their local calendar). Caps: days 1-7 1,000/day · 8-14 1,300/day · 15-21 2,150/day and 15,000/week · day 22+ uncapped.{' '}
            <span className="font-medium text-gray-500">Today vs ramp cap</span> counts OUTREACH only — replies are never
            blocked and consume no ramp budget. <span className="text-amber-600 font-medium">wk</span> = the weekly ceiling is
            binding before the daily one.
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Voice AI Usage panel ────────────────────────────────────────────────────
// Per tenant: did they finish Settings > Voice Channel, did they create a
// non-bulk (voice-eligible) campaign, are they actually using voice on a
// follow-up step TODAY (live only — a step can be set to Voice and still be
// silently sending SMS if the campaign's number was never imported into
// Retell, see resolveFollowupChannel in send-followups/index.ts), and minutes
// used vs. their plan's included minutes. Backed by
// GET /api/billing/voice-overview, which shares its minutes math with the
// tenant's own Billing page (computeVoiceUsage in billing.js) so the two can
// never disagree.
function BoolCell({ value, trueLabel, falseLabel }) {
  return value ? (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
      <CheckCircle className="w-3.5 h-3.5" /> {trueLabel}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
      <XCircle className="w-3.5 h-3.5" /> {falseLabel}
    </span>
  );
}

function VoiceUsagePanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('voice_minutes_used');
  const [sortDir, setSortDir] = useState('desc');

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);
      const res = await fetchWithCsrf('/api/billing/voice-overview');
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setRows(data.tenants || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(p => (p === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'tenant_name' ? 'asc' : 'desc'); }
  };

  const filtered = rows
    .filter(r => !search || (r.tenant_name || '').toLowerCase().includes(search.toLowerCase()))
    .slice()
    .sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'tenant_name') cmp = (a.tenant_name || '').localeCompare(b.tenant_name || '');
      else cmp = ((a[sortKey] ?? -1) - (b[sortKey] ?? -1));
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const voiceEnabledCount = rows.filter(r => r.voice_setup_done).length;
  const usingFollowupsCount = rows.filter(r => r.using_voice_on_followups).length;

  const SortHeader = ({ k, children, align = 'left' }) => (
    <th
      onClick={() => handleSort(k)}
      className={`px-4 py-3 text-${align} text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortKey === k && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </span>
    </th>
  );

  return (
    <>
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Tenants</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{rows.length}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Voice Channel set up</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{voiceEnabledCount}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Using voice on follow-ups (live)</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{usingFollowupsCount}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="relative w-72 max-w-full">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tenants…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="flex items-center px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error ? (
          <div className="p-6 text-sm text-red-600">{error}</div>
        ) : loading ? (
          <div className="p-6 text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No tenants found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <SortHeader k="tenant_name">Tenant</SortHeader>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Voice Channel setup</th>
                  <SortHeader k="nonbulk_campaign_count">Non-bulk campaign</SortHeader>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Voice on follow-ups</th>
                  <SortHeader k="voice_minutes_used">Minutes used / plan</SortHeader>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((r) => {
                  const pct = r.voice_minutes_limit ? Math.round((r.voice_minutes_used / r.voice_minutes_limit) * 1000) / 10 : null;
                  return (
                    <tr key={r.tenant_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{r.tenant_name || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 capitalize">{r.plan || '—'}</td>
                      <td className="px-4 py-3">
                        <BoolCell value={r.voice_setup_done} trueLabel={`Set up ${fmtDate(r.voice_setup_at)}`} falseLabel="Not set up" />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {r.nonbulk_campaign_count > 0 ? (
                          <BoolCell value={true} trueLabel={`${r.nonbulk_campaign_count} campaign${r.nonbulk_campaign_count === 1 ? '' : 's'}`} />
                        ) : (
                          <BoolCell value={false} falseLabel="None" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <BoolCell value={r.using_voice_on_followups} trueLabel="Live" falseLabel="No" />
                      </td>
                      <td className="px-4 py-3">
                        {r.voice_minutes_limit == null ? (
                          <span className="text-xs text-gray-400">voice not set up</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-sm tabular-nums text-gray-700">
                              {r.voice_minutes_used.toLocaleString()} / {r.voice_minutes_limit.toLocaleString()}
                            </span>
                            <UsageBar pct={pct} />
                            {r.voice_overage_minutes > 0 && (
                              <span className="text-xs font-semibold text-orange-600">
                                +{r.voice_overage_minutes.toLocaleString()} over (~${r.voice_overage_cost_estimate.toFixed(2)})
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
          <span className="font-medium text-gray-500">Voice on follow-ups</span> only counts a campaign as live if a Follow-up step
          is set to Voice AND that campaign's number has actually been imported into Retell — a step can be set to Voice and still
          silently send SMS otherwise. <span className="font-medium text-gray-500">Minutes used / plan</span> is the same figure and
          the same cycle math the tenant sees on their own Billing page, so this can never disagree with what Stripe bills.
        </div>
      </div>
    </>
  );
}

// ─── Integrations panel ──────────────────────────────────────────────────────
// Per-tenant integration adoption + error status. The in-app pull counterpart to
// the integration-monitor email watchdog: who's connected (stickiness), who's
// erroring (churn risk), who's a new account struggling to activate.

const INTEG_STATUS_CFG = {
  erroring:       { label: 'Erroring',      classes: 'bg-red-100 text-red-800',      Icon: AlertTriangle },
  disconnected:   { label: 'Outbound off',  classes: 'bg-orange-100 text-orange-800', Icon: XCircle },
  struggling:     { label: 'Struggling',    classes: 'bg-amber-100 text-amber-800',  Icon: Clock },
  live:           { label: 'Live',          classes: 'bg-green-100 text-green-800',  Icon: CheckCircle },
  connected_idle: { label: 'Connected idle',classes: 'bg-blue-100 text-blue-800',    Icon: Link2 },
  not_connected:  { label: 'Not connected', classes: 'bg-gray-100 text-gray-500',    Icon: XCircle },
};

function IntegStatusBadge({ status }) {
  const { label, classes, Icon } = INTEG_STATUS_CFG[status] || INTEG_STATUS_CFG.not_connected;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${classes}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function IntegrationsPanel() {
  const [rows, setRows] = useState([]);
  const [unattributed, setUnattributed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);
      const res = await fetchWithCsrf('/api/admin/integrations/health');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setRows(data.tenants || []);
      setUnattributed(data.unattributed_errors_7d || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const counts = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
  const connectedCount = rows.filter(r => r.status !== 'not_connected' && r.status !== 'disconnected').length;

  const filtered = rows
    .filter(r => statusFilter === 'all' || r.status === statusFilter)
    .filter(r => !search || (r.tenant_name || '').toLowerCase().includes(search.toLowerCase()));

  const STATUS_FILTERS = [
    { value: 'all', label: 'All statuses' },
    { value: 'erroring', label: 'Erroring' },
    { value: 'disconnected', label: 'Outbound off' },
    { value: 'struggling', label: 'Struggling' },
    { value: 'live', label: 'Live' },
    { value: 'connected_idle', label: 'Connected idle' },
    { value: 'not_connected', label: 'Not connected' },
  ];

  return (
    <>
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Connected</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{connectedCount}<span className="text-base font-normal text-gray-400"> / {rows.length}</span></div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Live</div>
          <div className="text-2xl font-bold text-green-600 mt-1">{counts.live || 0}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Erroring (5xx, 7d)</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{counts.erroring || 0}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Struggling</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{counts.struggling || 0}</div>
        </div>
      </div>

      {unattributed > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {unattributed} unattributed error(s) in the last 7 days — invalid API keys with no tenant resolved (often a mistyped key during setup).
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 gap-3">
          <div className="relative w-72 max-w-full">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tenants…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {STATUS_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="flex items-center px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {error ? (
          <div className="p-6 text-sm text-red-600">{error}</div>
        ) : loading ? (
          <div className="p-6 text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No tenants match.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tenant</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  {/* The two directions fail independently and for different
                      reasons, so they get their own columns. Inbound = their CRM
                      calling us (leads in). Outbound = us writing to their CRM
                      (hot leads out). One "delivery path" column hid the case
                      where outbound works perfectly and inbound has never fired. */}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" title="Their CRM calling us — leads coming in">Inbound</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" title="Us writing to their CRM — hot leads going out">Outbound</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Errors (7d)</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((r) => (
                  <tr key={r.tenant_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{r.tenant_name || r.tenant_id}</div>
                      <div className="text-xs text-gray-400">
                        {r.is_new_key ? `key ${r.key_age_days ?? '?'}d old` : `joined ${formatDate(r.created_at)}`}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap"><IntegStatusBadge status={r.status} /></td>
                    {/* INBOUND — their CRM calling us. Time comes from the tenant's
                        sfx_ key: every inbound source stamps that ONE field, so with
                        more than one source it cannot be attributed and we say so
                        rather than naming one at random. */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {(r.inbound_sources || []).length === 0 ? (
                        <span className="text-sm text-gray-400">—</span>
                      ) : (
                        <>
                          {r.last_used_at ? (
                            <span className={`text-sm ${r.live_zap ? 'text-green-600 font-medium' : 'text-gray-600'}`}>{timeAgo(r.last_used_at)}</span>
                          ) : (
                            <span className="text-sm text-amber-600" title="This tenant has an inbound path configured but it has never delivered a lead">never</span>
                          )}
                          <div
                            className="text-xs text-gray-400"
                            title={
                              (r.inbound_sources || []).length > 1
                                ? `Could be any of: ${r.inbound_sources.join(', ')} — all inbound sources stamp the same API key`
                                : undefined
                            }
                          >
                            {r.inbound_label || 'inbound'}
                            {(r.inbound_sources || []).length > 1 ? ' ?' : ''}
                          </div>
                        </>
                      )}
                    </td>
                    {/* OUTBOUND — us writing to their CRM. Proven only by a real
                        successful delivery, never by the connection merely existing. */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.connection_count === 0 && r.outbound_off_since ? (
                        <>
                          <span className="text-sm font-medium text-orange-600" title="Their Zapier connection was lost and nothing is enabled to deliver hot leads">off {timeAgo(r.outbound_off_since)}</span>
                          {r.undelivered_hot_7d > 0 && (
                            <div className="text-xs font-semibold text-red-600" title="Hot leads that fired after the connection was lost and had nowhere to go (last 7 days)">
                              {r.undelivered_hot_7d} hot lead{r.undelivered_hot_7d === 1 ? '' : 's'} undelivered
                            </div>
                          )}
                        </>
                      ) : r.connection_count === 0 ? (
                        <span className="text-sm text-gray-400">—</span>
                      ) : (
                        <>
                          {r.last_delivery_success_at ? (
                            <span className="text-sm text-gray-600">{timeAgo(r.last_delivery_success_at)}</span>
                          ) : (
                            <span className="text-sm text-amber-600" title="Connected, but no successful delivery on record">never</span>
                          )}
                          {r.delivery_failures_7d > 0 && (
                            <span className="ml-1 text-xs font-semibold text-red-600" title={`${r.delivery_failures_7d} failed deliveries in 7d`}>
                              {r.delivery_failures_7d} failed
                            </span>
                          )}
                          <div className="text-xs text-gray-400">{r.connection_types.join(', ')}</div>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-2 justify-end">
                        {r.errors_5xx_7d > 0 && <span className="text-xs font-semibold text-red-600" title="inbound server-side 5xx">{r.errors_5xx_7d}× 5xx</span>}
                        {r.delivery_failures_7d > 0 && <span className="text-xs font-semibold text-red-600" title="outbound webhook/CRM delivery failures">{r.delivery_failures_7d}× delivery</span>}
                        {r.errors_4xx_7d > 0 && <span className="text-xs font-semibold text-amber-600" title="inbound client-side 4xx">{r.errors_4xx_7d}× 4xx</span>}
                        {r.errors_5xx_7d === 0 && r.errors_4xx_7d === 0 && r.delivery_failures_7d === 0 && <span className="text-xs text-gray-300">none</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {r.last_error ? (
                        <div className="max-w-[280px]">
                          <p className="text-xs text-gray-700 truncate" title={r.last_error.message}>
                            <span className={(r.last_error.class === '5xx' || r.last_error.class === 'delivery') ? 'text-red-600 font-semibold' : 'text-amber-600 font-semibold'}>{r.last_error.status_code ?? 'ERR'}</span>
                            {' '}{r.last_error.surface}
                          </p>
                          <p className="text-xs text-gray-400">{timeAgo(r.last_error.occurred_at)} · {r.last_error.message}</p>
                        </div>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
          <span className="font-medium text-gray-500">Inbound</span> = their CRM calling us (leads in), timed off the tenant's SurFox API key. Every inbound source stamps that one key, so where a tenant has more than one the source shows as <span className="font-medium text-gray-500">inbound ?</span> rather than guessing.{' '}
          <span className="font-medium text-gray-500">Outbound</span> = us writing to their CRM (hot leads out), proven only by a real successful delivery.{' '}
          A tenant can be perfectly healthy outbound while inbound has <span className="font-medium text-gray-500">never</span> fired — that means nobody has moved a contact into the mapped stage yet.{' '}
          <span className="font-medium text-gray-500">Live</span> = an API key used in the last 30 min, or a connection that delivered successfully in the last 7 days.{' '}
          <span className="font-medium text-gray-500">Outbound off</span> = their Zapier connection was lost (Zap turned off or removed) and nothing else is enabled, so hot leads have nowhere to go. Shows when it went off and how many hot leads fired since.{' '}
          <span className="font-medium text-gray-500">Struggling</span> = inbound client-side (4xx) errors on a new/idle account.{' '}
          <span className="font-medium text-gray-500">Erroring</span> = inbound 5xx or outbound delivery failures — investigate.
        </div>
      </div>
    </>
  );
}

// ─── Deliverability panel ────────────────────────────────────────────────────
//
// Carrier filtering is invisible at the account level: a healthy account absorbs
// one poisoned template and still reports a mediocre-but-survivable aggregate.
// On 2026-07-20 one opener was blocked at 71% all day while the tenant's overall
// rate read 10.9%, and nothing surfaced it. So this panel leads with TEMPLATES,
// not tenants — that is the grain where the failure is actually legible.

function RatePill({ rate, flagged }) {
  const tone = flagged
    ? 'bg-red-100 text-red-700'
    : rate >= 5
      ? 'bg-amber-100 text-amber-700'
      : 'bg-green-100 text-green-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${tone}`}>
      {rate}%
    </span>
  );
}

// Leads whose message from this template never landed. The whole point of the
// panel is triage, and triage ends at a specific conversation — so every row is
// a link to the lead, with the raw id kept visible for DB queries and support
// threads where a link is useless.
function AffectedLeads({ row }) {
  const [showAll, setShowAll] = useState(false);
  const [copied, setCopied] = useState(false);
  const leads = row.affected_leads || [];
  if (leads.length === 0) return null;

  const visible = showAll ? leads : leads.slice(0, 10);

  const copyIds = async () => {
    try {
      await navigator.clipboard.writeText(leads.map(l => l.lead_id).join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — the ids are on screen anyway */ }
  };

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold text-gray-500 uppercase">
          Affected leads ({row.affected_lead_count})
          {row.affected_leads_truncated && (
            <span className="ml-1 normal-case font-normal text-gray-400">
              — showing first {leads.length}
            </span>
          )}
        </div>
        <button onClick={copyIds} className="text-xs text-indigo-600 hover:text-indigo-800">
          {copied ? 'Copied' : 'Copy IDs'}
        </button>
      </div>
      <div className="bg-white border border-gray-200 rounded divide-y divide-gray-100">
        {visible.map(l => (
          <div key={l.lead_id} className="flex items-center gap-3 px-3 py-2 text-xs">
            <span
              className={`px-1.5 py-0.5 rounded font-semibold flex-shrink-0 ${
                l.error_code === '30007'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              {l.error_code || l.delivery_status || 'failed'}
            </span>
            <a
              href={`/lead/${l.lead_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-800 font-medium truncate max-w-[12rem]"
            >
              {l.lead_name || 'Unnamed lead'}
            </a>
            <span className="font-mono text-gray-400 truncate">{l.lead_id}</span>
            <span className="ml-auto text-gray-400 whitespace-nowrap flex-shrink-0">
              {l.attempts > 1 && <span className="mr-2">×{l.attempts}</span>}
              {formatDateTime(l.last_failed_at)}
            </span>
          </div>
        ))}
      </div>
      {leads.length > 10 && (
        <button
          onClick={() => setShowAll(s => !s)}
          className="mt-2 text-xs text-indigo-600 hover:text-indigo-800"
        >
          {showAll ? 'Show fewer' : `Show all ${leads.length}`}
        </button>
      )}
    </div>
  );
}

function DeliverabilityPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [expanded, setExpanded] = useState({});
  const [onlyFlagged, setOnlyFlagged] = useState(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);
      const res = await fetchWithCsrf(`/api/admin/deliverability?days=${days}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Near-real-time without hammering the DB. Carrier filtering develops over
  // minutes, not seconds, so a 60s cadence catches a bad send within the first
  // wave or two rather than at end of day.
  useEffect(() => {
    const id = setInterval(() => fetchData(true), 60000);
    return () => clearInterval(id);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Loading deliverability…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex items-center">
        <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" /> {error}
      </div>
    );
  }

  const t = data?.totals || {};
  const flagged = (data?.templates || []).filter(x => x.flagged);
  const templates = onlyFlagged ? flagged : (data?.templates || []);

  return (
    <>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex gap-1 bg-white border border-gray-300 rounded-lg p-1">
          {[1, 7, 30].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                days === d ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {d === 1 ? '24h' : `${d}d`}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={onlyFlagged}
            onChange={e => setOnlyFlagged(e.target.checked)}
            className="rounded border-gray-300"
          />
          Flagged only
        </label>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="flex items-center px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <span className="text-xs text-gray-400 ml-auto">
          auto-refreshes every 60s · updated {data?.generated_at ? new Date(data.generated_at).toLocaleTimeString() : '—'}
        </span>
      </div>

      {/* Alert banner — the thing that should have existed on 07-20 */}
      {flagged.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <ShieldAlert className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-red-800">
                {flagged.length} template{flagged.length === 1 ? '' : 's'} being blocked by carriers
              </div>
              <p className="text-sm text-red-700 mt-1">
                Blocked messages are billed but never reach the lead, and the lead is
                not retried. Rewriting the opener is usually the fix.
              </p>
              <ul className="mt-3 space-y-1.5">
                {flagged.slice(0, 5).map((f, i) => (
                  <li key={i} className="text-sm text-red-900">
                    <span className="font-semibold">{f.block_rate}% blocked</span>
                    <span className="text-red-700"> · {f.blocked}/{f.sent} · {f.tenant_name} · {f.campaign_name}</span>
                    <div className="text-xs text-red-800 font-mono mt-0.5 truncate">{f.template}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Sent ({data?.window_days}d)</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{(t.sent || 0).toLocaleString()}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Delivered</div>
          <div className="text-2xl font-bold text-green-600 mt-1">{t.delivered_rate || 0}%</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Carrier-blocked (30007)</div>
          <div className={`text-2xl font-bold mt-1 ${(t.block_rate || 0) >= 5 ? 'text-red-600' : 'text-gray-900'}`}>
            {t.block_rate || 0}%
          </div>
          <div className="text-xs text-gray-400 mt-1">{(t.blocked || 0).toLocaleString()} messages</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Flagged templates</div>
          <div className={`text-2xl font-bold mt-1 ${flagged.length ? 'text-red-600' : 'text-gray-900'}`}>
            {t.flagged_templates || 0}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            ≥{data?.thresholds?.block_rate_alert_pct}% on ≥{data?.thresholds?.min_sends_for_verdict} sends
          </div>
        </div>
      </div>

      {/* Daily trend */}
      {/*
        CARRIER BLOCKS OWN THIS CHART. Two failures land in `daily`, and they must
        not share a scale:
          30007  — carrier spam-filtering. The copy is poisoned. Rare in volume
                   (260 in 30d) but catastrophic, and a spike is a same-day fix.
          3000x  — landline / dead number. A known, ACCEPTED cost since the
                   line-type scrub was switched off for price. Not actionable.
        Stacking them on one axis buries the signal under the accepted noise: a
        70%-undeliverable day crushes a 10% block day into a sliver. So blocks are
        scaled to their OWN max and drawn in front, undeliverable is a muted
        backdrop for context only, and ANY day with at least one block keeps a
        visible red floor — a single spam block must never render as zero.
      */}
      {(data?.daily || []).length > 1 && (() => {
        const worstBlock = Math.max(5, ...data.daily.map(d => d.block_rate || 0));
        const worstUnd   = Math.max(5, ...data.daily.map(d => d.undelivered_rate || 0));
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-gray-700">Daily carrier block rate (30007)</div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm bg-red-500" /> Carrier-blocked
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm bg-gray-200" /> Undeliverable (context)
                </span>
              </div>
            </div>
            <div className="flex items-end gap-2 h-28">
              {data.daily.map(d => {
                const blocked = d.blocked || 0;
                // Floor of 8% so one block in a thousand sends is still a red mark.
                const blockH = blocked > 0
                  ? Math.max(8, Math.min(100, ((d.block_rate || 0) / worstBlock) * 100))
                  : 0;
                const undH = Math.min(100, ((d.undelivered_rate || 0) / worstUnd) * 100);
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                    <div className="relative w-full h-full flex items-end">
                      <div
                        className="absolute bottom-0 w-full rounded-t bg-gray-200"
                        style={{ height: `${undH}%` }}
                      />
                      <div
                        className={`relative w-full rounded-t ${blocked ? 'bg-red-500' : 'bg-green-400'}`}
                        style={{ height: blocked ? `${blockH}%` : '2px' }}
                      />
                    </div>
                    <div className={`text-[10px] mt-1 ${blocked ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                      {d.date.slice(5)}
                    </div>
                    <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                      <div className={blocked ? 'font-semibold text-red-300' : 'font-semibold'}>
                        Blocked {d.block_rate}% · {blocked}/{d.sent}
                      </div>
                      <div className="text-gray-300">
                        Undeliverable {d.undelivered_rate}% · {d.undelivered || 0}/{d.sent}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Templates */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-700">By template</div>
          <div className="text-xs text-gray-400">{templates.length} shown</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Template</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Campaign</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Sent</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Blocked</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Block rate</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Undeliv.</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">No outbound messages in this window.</td></tr>
              )}
              {templates.map((row, i) => {
                const key = `${row.tenant_id}-${row.campaign_id}-${i}`;
                const open = expanded[key];
                return (
                  <React.Fragment key={key}>
                    <tr className={row.flagged ? 'bg-red-50' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-md">
                        <div className="flex items-start gap-2">
                          {row.flagged && <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />}
                          <span className="font-mono text-xs truncate">{row.template}</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">{row.tenant_name}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.campaign_name}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{row.sent.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{row.blocked.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right"><RatePill rate={row.block_rate} flagged={row.flagged} /></td>
                      <td className="px-4 py-3 text-sm text-right text-gray-500">{row.undelivered_rate}%</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setExpanded(p => ({ ...p, [key]: !p[key] }))}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-gray-50">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Sample message</div>
                          <div className="text-sm text-gray-800 bg-white border border-gray-200 rounded p-3 mb-3 whitespace-pre-wrap">{row.sample}</div>
                          <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                            <span>First sent: {formatDateTime(row.first_seen)}</span>
                            <span>Last sent: {formatDateTime(row.last_seen)}</span>
                            <span>Delivered: {row.delivered_rate}%</span>
                          </div>
                          {Object.keys(row.errors || {}).length > 0 && (
                            <div className="mt-3">
                              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Error codes</div>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(row.errors).sort((a, b) => b[1] - a[1]).map(([code, n]) => (
                                  <span key={code} className={`px-2 py-0.5 rounded text-xs ${code === '30007' ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'}`}>
                                    {code} × {n}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          <AffectedLeads row={row} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ─── AI Learning ─────────────────────────────────────────────────────────────
// Per-tenant state of the Thompson-sampling bandit. Built after the bandit ran dark
// for six weeks with every cron reporting green — cron health proves a job EXECUTED,
// never that the system LEARNED. See src/api_routes/admin-learning.js for the why.
//
// Aggregate only: counts, timestamps, spreads. Never arm strategy fields — those are
// the mechanism in the pending patent claims, so this page stays non-disclosing.

const LEARNING_STATUS_CFG = {
  gap:              { label: 'Gap',            classes: 'bg-red-100 text-red-800',        Icon: AlertTriangle, hint: 'Plan includes conversation learning but every stage flag is off. This tenant is paying for a feature they are not receiving.' },
  stale:            { label: 'Stale',          classes: 'bg-red-100 text-red-800',        Icon: AlertTriangle, hint: 'Enabled and actively sending, but no bandit decision recorded recently. Something is broken.' },
  over_provisioned: { label: 'Over-provisioned', classes: 'bg-amber-100 text-amber-800',  Icon: AlertTriangle, hint: 'Learning is on but the plan does not include it. A paid feature is being given away.' },
  partial:          { label: 'Partial',        classes: 'bg-amber-100 text-amber-800',    Icon: Gauge,         hint: 'Only some of the three stages are enabled.' },
  awaiting_first:   { label: 'Awaiting first', classes: 'bg-blue-100 text-blue-800',      Icon: Clock,         hint: 'Enabled, but no decision recorded yet. Normal for a tenant just switched on.' },
  warming:          { label: 'Warming',        classes: 'bg-blue-100 text-blue-800',      Icon: Clock,         hint: 'Decisions are flowing but no arm has reached 20 observations yet.' },
  learning:         { label: 'Learning',       classes: 'bg-green-100 text-green-800',    Icon: CheckCircle,   hint: 'Enabled, decisions flowing, and arms are accumulating enough data to discriminate.' },
  not_entitled:     { label: 'Not entitled',   classes: 'bg-gray-100 text-gray-600',      Icon: Lock,          hint: 'Plan does not include conversation learning, and it is correctly off.' },
  sandbox:          { label: 'Sandbox',        classes: 'bg-gray-100 text-gray-600',      Icon: Lock,          hint: 'Demo tenant, deliberately held off so stress tests stay reproducible. Not a gap.' },
};

function LearningStatusBadge({ status }) {
  const cfg = LEARNING_STATUS_CFG[status] || LEARNING_STATUS_CFG.not_entitled;
  const { label, classes, Icon, hint } = cfg;
  return (
    <span title={hint} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${classes}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

/**
 * Separation is the only number on this page that proves the bandit is DISCRIMINATING
 * rather than merely spinning. It is the median best/worst posterior ratio across
 * context cells that cleared the sample thresholds. Flat at ~1.0 after a month means
 * running but not learning.
 */
function SeparationCell({ separation, cells }) {
  if (separation == null) {
    return (
      <div>
        <span className="text-sm text-gray-400">—</span>
        <div className="text-xs text-gray-400">no converged cells</div>
      </div>
    );
  }
  const tone = separation >= 1.5 ? 'text-green-600 font-medium'
    : separation >= 1.15 ? 'text-gray-700'
    : 'text-amber-600';
  return (
    <div>
      <span className={`text-sm ${tone}`} title="Median best/worst posterior mean across converged context cells">
        {separation.toFixed(2)}×
      </span>
      <div className="text-xs text-gray-400">{cells} cell{cells === 1 ? '' : 's'}</div>
    </div>
  );
}

function LearningPanel() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);
      const res = await fetchWithCsrf('/api/admin/learning/overview');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setRows(data.tenants || []);
      setTotals(data.totals || null);
      setThresholds(data.thresholds || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const STATUS_FILTERS = [
    { value: 'all', label: 'All statuses' },
    { value: 'needs_action', label: 'Needs action' },
    ...Object.entries(LEARNING_STATUS_CFG).map(([value, c]) => ({ value, label: c.label })),
  ];

  const NEEDS_ACTION = ['gap', 'stale', 'over_provisioned'];

  const filtered = rows
    .filter(r => statusFilter === 'all'
      || (statusFilter === 'needs_action' ? NEEDS_ACTION.includes(r.status) : r.status === statusFilter))
    .filter(r => !search || (r.tenant_name || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      {/* Summary cards. `Gaps` is the number that should always be zero — it is the
          failure that actually happened, and it is true on a tenant's first day. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500" title="Entitled by plan but every stage flag is off">Entitlement gaps</div>
          <div className={`text-2xl font-bold mt-1 ${totals?.gaps ? 'text-red-600' : 'text-gray-900'}`}>{totals?.gaps ?? '—'}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500" title="Enabled and sending, but no recent decision">Stale</div>
          <div className={`text-2xl font-bold mt-1 ${totals?.stale ? 'text-red-600' : 'text-gray-900'}`}>{totals?.stale ?? '—'}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Learning</div>
          <div className="text-2xl font-bold text-green-600 mt-1">{totals?.learning ?? '—'}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Decisions (30d)</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{(totals?.decisions_30d ?? 0).toLocaleString()}</div>
        </div>
      </div>

      {totals?.gaps > 0 && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            {totals.gaps} tenant(s) are entitled to conversation learning but have it switched off.
            Nothing in the product writes these flags — they are hand-inserted rows in
            <code className="mx-1 px-1 bg-red-100 rounded text-xs">platform_settings</code>
            (<code className="px-1 bg-red-100 rounded text-xs">learning_bandit_&#123;initial,followup,reply&#125;_enabled</code> = <code className="px-1 bg-red-100 rounded text-xs">'true'</code>),
            so a new signup stays off until someone does it by hand.
          </span>
        </div>
      )}

      {totals?.pending_rewards > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-center gap-2">
          <Clock className="w-4 h-4 flex-shrink-0" />
          {totals.pending_rewards} decision(s) still awaiting a finalized reward. Normal below ~48h old; a
          growing number means <code className="mx-1 px-1 bg-amber-100 rounded text-xs">update-learning-outcomes</code> has stopped closing the loop.
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 gap-3">
          <div className="relative w-72 max-w-full">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tenants…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {STATUS_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="flex items-center px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {error ? (
          <div className="p-6 text-sm text-red-600">{error}</div>
        ) : loading ? (
          <div className="p-6 text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No tenants match.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tenant</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  {/* Entitled vs Stages on is the load-bearing pair. Everything else on
                      this page needs volume before it says anything; this pair is
                      actionable the day a tenant signs up. */}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" title="Does the plan's feature matrix include aiLearning?">Entitled</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" title="Which of the three decision surfaces have the flag on">Stages on</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last decision</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Decisions (30d)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider" title="Arms with at least 20 observations">Arms learning</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" title="Median best/worst posterior ratio across converged cells — the only proof it is discriminating">Separation</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider" title="Should sit near 10%">Explore</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((r) => (
                  <tr key={r.tenant_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {r.tenant_name || r.tenant_id}
                        {r.is_demo && <span className="ml-2 text-xs text-gray-400">demo</span>}
                      </div>
                      <div className="text-xs text-gray-400">{r.plan}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap"><LearningStatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.entitled
                        ? <span className="text-sm text-gray-700">yes</span>
                        : <span className="text-sm text-gray-400">no</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.stages_on.length === 0 ? (
                        <span className={`text-sm ${r.entitled ? 'text-red-600 font-medium' : 'text-gray-400'}`}>none</span>
                      ) : (
                        <>
                          <span className="text-sm text-gray-700">{r.stages_on.join(', ')}</span>
                          {r.enabled_at && <div className="text-xs text-gray-400">since {formatDate(r.enabled_at)}</div>}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.last_decision_at ? (
                        <>
                          <span className={`text-sm ${r.status === 'stale' ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                            {timeAgo(r.last_decision_at)}
                          </span>
                          <div className="text-xs text-gray-400">{formatDateTime(r.last_decision_at)}</div>
                        </>
                      ) : (
                        <span className={`text-sm ${r.enabled ? 'text-amber-600' : 'text-gray-400'}`}>never</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <span className="text-sm text-gray-700">{r.decisions_30d.toLocaleString()}</span>
                      {r.decisions_30d > 0 && (
                        <div className="text-xs text-gray-400" title="initial / followup / reply">
                          {r.by_stage_30d.initial}/{r.by_stage_30d.followup}/{r.by_stage_30d.reply}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <span className="text-sm text-gray-700">{r.arms_learning.toLocaleString()}</span>
                      <div className="text-xs text-gray-400">of {r.arms_total.toLocaleString()}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <SeparationCell separation={r.separation} cells={r.converged_cells} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      {r.exploration_pct == null ? (
                        <span className="text-sm text-gray-400">—</span>
                      ) : (
                        <span className={`text-sm ${Math.abs(r.exploration_pct - 10) > 5 ? 'text-amber-600' : 'text-gray-600'}`}>
                          {r.exploration_pct}%
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {thresholds && (
          <div className="px-4 py-3 border-t border-gray-200 text-xs text-gray-400">
            Separation counts a cell only once it holds {thresholds.min_cell_samples}+ observations,
            and only compares arms with {thresholds.min_arm_samples}+ each — without that floor a
            single lucky reward on one send ranks top. Stale = enabled and sending but silent for {thresholds.stale_hours}h.
          </div>
        )}
      </div>
    </>
  );
}

// ─── Affiliates tab ──────────────────────────────────────────────────────────
// A risk view, not a duplicate of /admin/affiliates (the operational CRUD
// page for creating/editing/approving affiliates and marking commissions
// paid). This asks "which affiliate needs me to do something today" — same
// framing as every other tab here. Health is computed server-side
// (GET /api/affiliates/health-overview) from data across four tables: sign-ins
// (affiliate_auth_events), clicks (affiliate_clicks), commissions
// (affiliate_commissions), and the affiliate row itself.

const AFFILIATE_HEALTH_CFG = {
  healthy:  { label: 'Healthy',  classes: 'bg-green-100 text-green-800',  Icon: CheckCircle },
  at_risk:  { label: 'At Risk',  classes: 'bg-red-100 text-red-800',      Icon: AlertTriangle },
  dormant:  { label: 'Dormant',  classes: 'bg-yellow-100 text-yellow-800', Icon: Clock },
  disabled: { label: 'Disabled', classes: 'bg-gray-100 text-gray-500',    Icon: XCircle },
};

function AffiliateHealthBadge({ health }) {
  const { label, classes, Icon } = AFFILIATE_HEALTH_CFG[health] || AFFILIATE_HEALTH_CFG.healthy;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${classes}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function AffiliatesHealthPanel() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [healthFilter, setHealthFilter] = useState('all');
  const [sortKey, setSortKey] = useState('owed_cents');
  const [sortDir, setSortDir] = useState('desc');

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);
      const res = await fetchWithCsrf('/api/affiliates/health-overview');
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setRows(data.rows || []);
      setSummary(data.summary || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((p) => (p === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'name' ? 'asc' : 'desc'); }
  };

  const filtered = rows
    .filter((r) => !search || `${r.name} ${r.email}`.toLowerCase().includes(search.toLowerCase()))
    .filter((r) => healthFilter === 'all' || r.health === healthFilter)
    .slice()
    .sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = (a.name || '').localeCompare(b.name || '');
      else if (sortKey === 'last_sign_in') cmp = (a.last_sign_in ? new Date(a.last_sign_in).getTime() : 0) - (b.last_sign_in ? new Date(b.last_sign_in).getTime() : 0);
      else cmp = (a[sortKey] ?? -1) - (b[sortKey] ?? -1);
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const SortHeader = ({ k, children, align = 'left' }) => (
    <th
      onClick={() => handleSort(k)}
      className={`px-4 py-3 text-${align} text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortKey === k && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </span>
    </th>
  );

  if (loading) {
    return <div className="flex justify-center items-center py-24"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>;
  }

  return (
    <>
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Active affiliates</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{summary?.total_active ?? 0}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Pending applications</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">{summary?.pending_applications ?? 0}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">Total owed (held + unpaid)</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{money(summary?.total_owed_cents)}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500 flex items-center gap-1">
            At risk / Dormant
            <span title="At risk: locked out, or money owed with no sign-in in 30+ days. Dormant: approved 7+ days with zero clicks ever.">
              <AlertTriangle className="w-3.5 h-3.5 text-gray-400" />
            </span>
          </div>
          <div className="text-2xl font-bold mt-1">
            <span className="text-red-600">{summary?.at_risk ?? 0}</span>
            <span className="text-gray-300 mx-1">/</span>
            <span className="text-yellow-600">{summary?.dormant ?? 0}</span>
          </div>
        </div>
      </div>

      {/* Search + health filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search affiliates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
        <select value={healthFilter} onChange={(e) => setHealthFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="all">All health</option>
          <option value="at_risk">At Risk</option>
          <option value="dormant">Dormant</option>
          <option value="healthy">Healthy</option>
          <option value="disabled">Disabled</option>
        </select>
        <button onClick={() => fetchData(true)} disabled={refreshing} className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 ml-auto">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center">
          <XCircle className="w-5 h-5 text-red-600 mr-2" /><p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-12"><Handshake className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">No affiliates match</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <SortHeader k="name">Affiliate</SortHeader>
                  <SortHeader k="health">Health</SortHeader>
                  <SortHeader k="last_sign_in">Last Sign-In</SortHeader>
                  <SortHeader k="total_clicks" align="right">Clicks</SortHeader>
                  <SortHeader k="conversion_rate" align="right">Conv. Rate</SortHeader>
                  <SortHeader k="referrals_active" align="right">Active Refs</SortHeader>
                  <SortHeader k="owed_cents" align="right">Owed</SortHeader>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <div className="text-gray-900 font-medium">{r.name}</div>
                      <div className="text-gray-500 text-xs">{r.email}</div>
                    </td>
                    <td className="px-4 py-3"><AffiliateHealthBadge health={r.health} /></td>
                    <td className="px-4 py-3 text-sm text-gray-700">{timeAgo(r.last_sign_in)}</td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-700">{r.total_clicks}</td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-700">{r.conversion_rate === null ? '—' : `${r.conversion_rate}%`}</td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-700">{r.referrals_active}</td>
                    <td className={`px-4 py-3 text-sm text-right tabular-nums font-medium ${r.owed_cents > 0 ? 'text-amber-700' : 'text-gray-500'}`}>{money(r.owed_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

const CustomerHealthDashboard = () => {
  const { user } = useAuth();
  const [tenants, setTenants]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [error, setError]               = useState(null);
  const [search, setSearch]             = useState('');
  const [healthFilter, setHealthFilter] = useState('all');
  const [onboardingFilter, setOnboardingFilter] = useState('all');
  const [expanded, setExpanded]         = useState({});
  const [sortKey, setSortKey]           = useState('last_active');
  const [sortDir, setSortDir]           = useState('desc');
  const [activeTab, setActiveTab]       = useState('tenants'); // 'tenants' | 'tools'

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'tenant' ? 'asc' : 'desc');
    }
  };

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (search) params.set('search', search);

      const res = await fetchWithCsrf(`/api/admin/onboarding/health?${params}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setTenants(data.tenants || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const healthOrder = { at_risk: 0, in_progress: 1, healthy: 2 };
  const filtered = tenants
    .filter(t => healthFilter === 'all' || t.health === healthFilter)
    .filter(t => onboardingFilter === 'all' || (t.onboarding_status || 'not_started') === onboardingFilter)
    .slice().sort((a, b) => {
    let cmp = 0;
    switch (sortKey) {
      case 'tenant':
        cmp = (a.tenant_name || '').localeCompare(b.tenant_name || '');
        break;
      case 'health':
        cmp = (healthOrder[a.health] ?? 1) - (healthOrder[b.health] ?? 1);
        break;
      case 'onboarding': {
        const obOrder = { not_started: 0, in_progress: 1, complete: 2 };
        cmp = (obOrder[a.onboarding_status] ?? 0) - (obOrder[b.onboarding_status] ?? 0);
        break;
      }
      case 'steps':
        cmp = (a.completed_steps || 0) - (b.completed_steps || 0);
        break;
      case 'subscription':
        cmp = (a.subscription_status || '').localeCompare(b.subscription_status || '');
        break;
      case 'last_active': {
        const aTime = a.details?.last_sign_in ? new Date(a.details.last_sign_in).getTime() : 0;
        const bTime = b.details?.last_sign_in ? new Date(b.details.last_sign_in).getTime() : 0;
        cmp = aTime - bTime;
        break;
      }
      case 'age': {
        const aAge = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bAge = b.created_at ? new Date(b.created_at).getTime() : 0;
        cmp = aAge - bAge;
        break;
      }
      default:
        break;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleExpand = (id) =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const counts = tenants.reduce((acc, t) => {
    acc[t.health] = (acc[t.health] || 0) + 1;
    return acc;
  }, {});

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <HeartPulse className="w-8 h-8 text-indigo-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Customer Health</h1>
              <p className="text-sm text-gray-600 mt-1">Track every tenant's onboarding progress and activation status</p>
            </div>
          </div>
          {/* Tenants only. fetchData reloads the Tenants list and nothing else —
              every other tab is a self-contained panel with its own fetch and its
              own Refresh, so showing this here spun a button that changed nothing
              the operator could see. */}
          {activeTab === 'tenants' && (
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          )}
        </div>
      </div>

      <div className="p-6">

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {[
            { key: 'tenants', label: 'Tenants', Icon: Users },
            { key: 'deliverability', label: 'Deliverability', Icon: ShieldAlert },
            { key: 'tuning', label: 'Talk Track Tuning', Icon: SlidersHorizontal },
            { key: 'integrations', label: 'Integrations', Icon: Zap },
            { key: 'learning', label: 'AI Learning', Icon: Brain },
            { key: 'billing', label: 'Billing & Usage', Icon: CreditCard },
            { key: 'voice',   label: 'Voice AI Usage', Icon: Phone },
            { key: 'affiliates', label: 'Affiliates', Icon: Handshake },
            { key: 'tools',   label: 'Internal Tools', Icon: Boxes },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === key
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'tools' ? (
          <InternalToolsPanel />
        ) : activeTab === 'deliverability' ? (
          <DeliverabilityPanel />
        ) : activeTab === 'tuning' ? (
          <TuningPanel />
        ) : activeTab === 'learning' ? (
          <LearningPanel />
        ) : activeTab === 'integrations' ? (
          <IntegrationsPanel />
        ) : activeTab === 'billing' ? (
          <BillingUsagePanel />
        ) : activeTab === 'voice' ? (
          <VoiceUsagePanel />
        ) : activeTab === 'affiliates' ? (
          <AffiliatesHealthPanel />
        ) : (
        <>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Tenants</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{tenants.length}</p>
              </div>
              <Users className="w-10 h-10 text-indigo-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Healthy</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{counts.healthy || 0}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{counts.in_progress || 0}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">At Risk</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{counts.at_risk || 0}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Table card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

          {/* Table toolbar */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Tenant Onboarding Status</h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchData()}
                  placeholder="Search tenant name…"
                  className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <select
                value={healthFilter}
                onChange={e => setHealthFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {HEALTH_FILTERS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
              <select
                value={onboardingFilter}
                onChange={e => setOnboardingFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {ONBOARDING_FILTERS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <HeartPulse className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No tenants match your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {[
                      { key: 'tenant',       label: 'Tenant' },
                      { key: 'onboarding',   label: 'Onboarding' },
                      { key: 'health',       label: 'Health' },
                      { key: null,           label: 'Progress' },
                      { key: 'steps',        label: 'Steps' },
                      { key: 'subscription', label: 'Subscription' },
                      { key: 'last_active',  label: 'Last Active' },
                      { key: 'age',          label: 'Age' },
                      { key: null,           label: 'Anthropic' },
                    ].map(col => (
                      <th
                        key={col.label}
                        onClick={col.key ? () => handleSort(col.key) : undefined}
                        className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          col.key ? 'cursor-pointer select-none hover:text-gray-900 text-gray-500' : 'text-gray-500'
                        }`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {col.key && sortKey === col.key && (
                            sortDir === 'asc'
                              ? <ChevronUp className="w-3 h-3" />
                              : <ChevronDown className="w-3 h-3" />
                          )}
                        </span>
                      </th>
                    ))}
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.map(tenant => (
                    <React.Fragment key={tenant.tenant_id}>
                      <tr
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => toggleExpand(tenant.tenant_id)}
                      >
                        {/* Tenant name */}
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-900">{tenant.tenant_name || tenant.tenant_id}</p>
                          <p className="text-xs text-gray-500 mt-0.5">Joined {formatDate(tenant.created_at)}</p>
                        </td>

                        {/* Onboarding status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <OnboardingBadge status={tenant.onboarding_status} />
                          {tenant.onboarding_status === 'in_progress' && tenant.onboarding_started_at && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              Started {timeAgo(tenant.onboarding_started_at)}
                            </p>
                          )}
                        </td>

                        {/* Health */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <HealthBadge health={tenant.health} />
                        </td>

                        {/* Progress dots */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <ProgressDots steps={tenant.steps} />
                        </td>

                        {/* Steps count */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-700">
                            {tenant.completed_steps}/{tenant.total_steps}
                          </span>
                        </td>

                        {/* Subscription */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            tenant.subscription_status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : tenant.subscription_status === 'trialing'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-600'
                          }`}>
                            {tenant.subscription_status || 'unknown'}
                          </span>
                        </td>

                        {/* Last Active */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <span className={`text-sm ${
                              !tenant.details.last_sign_in ? 'text-red-500 font-medium'
                                : daysSince(tenant.details.last_sign_in) > 3 ? 'text-yellow-600'
                                : 'text-gray-600'
                            }`}>
                              {timeAgo(tenant.details.last_sign_in)}
                            </span>
                            {tenant.details.last_sign_in && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                {formatDateTime(tenant.details.last_sign_in)}
                              </p>
                            )}
                          </div>
                        </td>

                        {/* Age */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{daysSince(tenant.created_at)}d</span>
                        </td>

                        {/* Anthropic */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <AnthropicBadge anthropic={tenant.anthropic} />
                        </td>

                        {/* Expand toggle */}
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {expanded[tenant.tenant_id]
                            ? <ChevronUp className="w-4 h-4 text-gray-400 inline" />
                            : <ChevronDown className="w-4 h-4 text-gray-400 inline" />
                          }
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {expanded[tenant.tenant_id] && (
                        <TenantDetail tenant={tenant} onChange={() => fetchData(true)} />
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        </>
        )}

      </div>
    </div>
  );
};

export default CustomerHealthDashboard;
